import { useEffect, useState } from "react";
import PageSeo from "@/components/seo/PageSeo";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Clock, Eye, ArrowRight, ArrowLeft, Search, Menu, X, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const TELEGRAM_LINK = "https://t.me/+QOePMys7V481NmZl";

const BlogListPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [mobileMenu, setMobileMenu] = useState(false);

  const navLinks = [
    { label: "Home", href: "/" },
    { label: "About", href: "/about" },
    { label: "Courses", href: "/#courses" },
    { label: "Blogs", href: "/blog" },
    { label: "Gallery", href: "/gallery" },
    { label: "Contact", href: "/contact" }
  ];

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await axios.get(`${API}/blogs`);
        setBlogs(res.data);
      } catch (err) {
        console.error("Error fetching blogs:", err); // eslint-disable-line no-console
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const filtered = blogs.filter(b =>
    b.title.toLowerCase().includes(search.toLowerCase()) ||
    b.excerpt.toLowerCase().includes(search.toLowerCase()) ||
    (b.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#0f1419]" data-testid="blog-list-page">
      <PageSeo path="/blog" fallbackTitle="Blog — Jamadhan" fallbackDescription="Read the latest articles, market insights, and trading strategies from Jamadhan." />
      {/* ═══ FULL SITE HEADER / NAVBAR ═══ */}
      <nav className="sticky top-0 z-50 px-4 sm:px-6 py-3.5 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.3)] border border-[#00FF88]/30 group-hover:border-[#00FF88]/70 transition-all duration-300">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-lg font-bold text-white font-outfit tracking-tight group-hover:text-[#00FF88] transition-colors">JAMADHAN</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((l) => (
              <Link 
                key={l.label} 
                to={l.href} 
                className={`text-sm font-medium tracking-wide transition-colors ${
                  l.href === '/blog' ? 'text-[#00FF88] font-bold' : 'text-[#CBD5E1] hover:text-[#00FF88]'
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/drishti-ai" className="text-sm text-[#00F0FF] hover:text-[#00FF88] transition-colors font-semibold flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Drishti AI
            </Link>
            <Button 
              onClick={() => navigate('/login')} 
              size="sm" 
              className="bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white hover:shadow-[0_0_20px_rgba(0,255,136,0.4)] border border-transparent hover:border-[#00FF88] rounded-sm px-5 transition-all duration-200"
            >
              Login
            </Button>
          </div>

          <button 
            onClick={() => setMobileMenu(!mobileMenu)} 
            className="md:hidden text-white p-1 hover:text-[#00FF88] transition-colors" 
            aria-label="Toggle Menu"
          >
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenu && (
          <div className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10 px-6 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <Link 
                key={l.label} 
                to={l.href} 
                onClick={() => setMobileMenu(false)}
                className={`text-sm font-medium py-1.5 transition-colors ${
                  l.href === '/blog' ? 'text-[#00FF88] font-bold' : 'text-[#CBD5E1] hover:text-[#00FF88]'
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link 
              to="/drishti-ai" 
              onClick={() => setMobileMenu(false)}
              className="text-sm text-[#00F0FF] hover:text-[#00FF88] transition-colors font-semibold flex items-center gap-1 py-1.5"
            >
              <Sparkles className="w-3.5 h-3.5" /> Drishti AI
            </Link>
            <Button 
              onClick={() => { setMobileMenu(false); navigate('/login'); }} 
              size="sm" 
              className="mt-2 w-full bg-[#00FF88] text-black font-bold"
            >
              Login
            </Button>
          </div>
        )}
      </nav>

      {/* Hero */}
      <div className="relative py-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00d991]/5 to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white font-outfit mb-4" data-testid="blog-title">
            Trading Insights & Education
          </h1>
          <p className="text-base text-[#b0b8c4] mb-8 max-w-2xl mx-auto">
            Expert articles on technical analysis, option trading, and market strategies
          </p>
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
            <Input
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#1a1f2e] border-[#2a3142] text-white placeholder:text-[#6b7280]"
              data-testid="blog-search"
            />
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#00d991] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#6b7280]">No articles found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((blog, i) => (
              <Link
                key={blog.blog_id}
                to={`/blog/${blog.slug}`}
                className="group bg-[#1a1f2e] border border-[#2a3142] rounded-xl overflow-hidden hover:border-[#00d991]/40 transition-all duration-300 hover:-translate-y-1"
                data-testid={`blog-card-${i}`}
              >
                {blog.cover_image && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={blog.cover_image}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {(blog.tags || []).slice(0, 3).map((tag, ti) => (
                      <Badge key={ti} className="bg-[#00d991]/10 text-[#00d991] border-0 text-xs">{tag}</Badge>
                    ))}
                  </div>
                  <h2 className="text-lg font-semibold text-white font-outfit mb-2 group-hover:text-[#00d991] transition-colors line-clamp-2">
                    {blog.title}
                  </h2>
                  <p className="text-sm text-[#6b7280] mb-4 line-clamp-2">{blog.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-[#6b7280]">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(blog.created_at)}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{blog.views || 0}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#00d991] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-[#2a3142] py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded overflow-hidden shadow-[0_0_18px_rgba(0,255,136,0.28)]">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-white font-semibold font-outfit tracking-tight">JAMADHAN</span>
          </div>
          <p className="text-sm text-[#6b7280]">&copy; 2025 Jamadhan. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogListPage;
